import React, { createContext, useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { useGetMetadata } from 'hooks/useGetMetadata';
import { useSlidingDelay } from 'hooks/useSlidingDelay';
import { MetadataApiResponse, User } from 'types';

export const AuthContext = createContext<{
  user?: MetadataApiResponse['user'];
  impersonator?: MetadataApiResponse['impersonator'];
  isAuthenticated: boolean;
  isLoading: boolean;
  isGeneralFeedbackSubmitted?: boolean;
  loginCount?: number;
}>({
  user: undefined,
  isAuthenticated: false,
  isLoading: false,
});

export const AuthProvider = (props: React.PropsWithChildren) => {
  const metadata = useGetMetadata();
  // react-router-dom can't be used here
  const isGa4ConfigPage = window.location.pathname.includes('/configure-ga4');

  const value = useMemo(
    () => ({
      user: metadata?.data?.user,
      impersonator: metadata?.data?.impersonator,
      isAuthenticated: !!metadata?.data?.user,
      isLoading: metadata?.isLoading,
      isGeneralFeedbackSubmitted: metadata?.data?.isGeneralFeedbackSubmitted,
      loginCount: metadata?.data?.loginCount,
    }),
    [metadata.data, metadata.isLoading]
  );

  const setSentryUser = useSlidingDelay((user?: User) => {
    if (isGa4ConfigPage) {
      return <>{props.children}</>;
    }

    if (!user) {
      Sentry.setUser(null);
      return null;
    }

    const { sub, email, firstName, lastName, isAdministrator } = user;
    Sentry.setUser({
      id: sub,
      email,
      firstName,
      lastName,
      isAdministrator,
    });
    return null;
  });

  useEffect(() => {
    setSentryUser(value.user);
  }, [setSentryUser, value.user]);

  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
};
